import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { DICTIONARIES, vi } from './dictionaries'
import type { Lang, TranslationKey } from './dictionaries'
import { LanguageContext } from './context'
import { setDateLocale } from '@/lib/time'
import type { LanguageValue } from './context'

const STORAGE_KEY = 'bella.lang'

function isLang(value: unknown): value is Lang {
  return value === 'vi' || value === 'en'
}

/**
 * Vietnamese is the default, not merely the fallback: it is what a visitor with
 * no stored preference gets unless their browser clearly asks for English.
 * Anyone else lands on Vietnamese and can switch with the globe button, which
 * is why that control is reachable before signing in.
 */
function initialLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isLang(stored)) return stored
  } catch {
    // Private mode, or storage disabled. Fall through to the default.
  }
  const preferred = typeof navigator !== 'undefined' ? navigator.languages : undefined
  for (const tag of preferred ?? []) {
    if (tag.toLowerCase().startsWith('vi')) return 'vi'
    if (tag.toLowerCase().startsWith('en')) return 'en'
  }
  return 'vi'
}

// Applied at import time, and again in setLang below. Doing it only in an
// effect would be too late: the effect runs after the render that a language
// change triggers, so that render would still format dates in the old
// language and nothing would re-render to correct it.
setDateLocale(initialLang())

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang)

  // Screen readers and the browser's own translation prompt both key off this.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    // Before the state update, so the very next render formats dates correctly.
    setDateLocale(next)
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Not being able to remember the choice is survivable; failing is not.
    }
  }, [])

  const value = useMemo<LanguageValue>(() => {
    const dict = DICTIONARIES[lang]
    return {
      lang,
      setLang,
      t: (key: TranslationKey, vars) => {
        // Falling back to Vietnamese rather than the key means a missing
        // translation reads as untranslated, never as `chats.title`.
        const template = dict[key] ?? vi[key] ?? key
        if (!vars) return template
        return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
          name in vars ? String(vars[name]) : whole,
        )
      },
    }
  }, [lang, setLang])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}
